import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useSearchParams, useLocation } from 'react-router-dom';
import { AppProvider } from './contexts/AppContext';
import { AuthProvider } from './contexts/AuthContext';
import { useAuth } from './contexts/AuthContext';
import { ProfileProvider, useProfile } from './contexts/ProfileContext';
import { getPublicProfile } from './services/profileService';
import Header from './components/Header';
import Footer from './components/Footer';
import PageNavigation from './components/PageNavigation';
import WeeklyBanner from './components/WeeklyBanner';
import GoogleSyncBanner from './components/GoogleSyncBanner';
import UsernameBanner from './components/UsernameBanner';
import ProfileBanner from './components/ProfileBanner';
import UsernameModal from './components/UsernameModal';
import { isTrustedPublisher, hasCreatedProfile } from './services/galleryService';
import RandomPalettePage from './pages/RandomPalettePage';
import ColorPickerPage from './pages/ColorPickerPage';
import BrandConverterPage from './pages/BrandConverterPage';
import HexFinderPage from './pages/HexFinderPage';
import MyCollectionPage from './pages/MyCollectionPage';
import MyPalettesPage from './pages/MyPalettesPage';
import GalleryPage from './pages/GalleryPage';
import LegalPage from './pages/LegalPage';
import SuggestionsPage from './pages/SuggestionsPage';
import ProfilePage from './pages/ProfilePage';
import ExpandCollectionPage from './pages/ExpandCollectionPage';
import ShadeFinderPage from './pages/ShadeFinderPage';
import ScrollToTop from './components/ScrollToTop';
import { loadBrands } from './utils/data';

// Fetches and decodes brands.dat before rendering the app.
// Shows a full-screen spinner while loading so no component tries to use
// empty markers/BRANDS arrays.
function BrandsLoader({ children }) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadBrands()
      .then(() => setReady(true))
      .catch(err => {
        console.error('Failed to load brand data:', err);
        setError(err);
      });
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500 text-sm">Failed to load data. Please refresh.</p>
      </div>
    );
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-pink-200 border-t-pink-500 rounded-full animate-spin" />
      </div>
    );
  }

  return children;
}

function ScrollReset() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Layout wrapper component
function AppLayout({ children }) {
  const { user } = useAuth();
  const { profileCreated, setProfileCreated, setProfileAvatar } = useProfile();
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [isTrusted, setIsTrusted] = useState(false);

  useEffect(() => {
    if (!user?.uid) {
      setIsTrusted(false);
      setProfileCreated(true);
      return;
    }
    isTrustedPublisher(user.uid).then(trusted => {
      setIsTrusted(trusted);
      if (trusted) {
        hasCreatedProfile(user.uid).then(created => {
          setProfileCreated(created);
          if (created && user.username) {
            getPublicProfile(user.username).then(result => {
              if (result.success) setProfileAvatar(result.profile.avatar ?? 0);
            });
          }
        });
      }
    });
  }, [user?.uid]);

  return (
    <div className="min-h-screen">
      <Header onOpenUsernameModal={() => setShowUsernameModal(true)} isTrusted={isTrusted} />
      <WeeklyBanner />
      <GoogleSyncBanner />
      {isTrusted && !profileCreated
        ? <ProfileBanner onSetUsername={() => setShowUsernameModal(true)} />
        : !isTrusted && <UsernameBanner onSetUsername={() => setShowUsernameModal(true)} />
      }
      <PageNavigation />
      <main className="max-w-7xl mx-auto px-6 md:px-8">
        {children}
      </main>
      <Footer />
      {/* <ScrollToTop /> */}
      <UsernameModal isOpen={showUsernameModal} onClose={() => setShowUsernameModal(false)} />
    </div>
  );
}

// Home page that handles both regular view and ?code= parameter
function HomePage() {
  return <RandomPalettePage />;
}

// Component to handle ?page=legal query param for OAuth compatibility
function QueryParamRouter() {
  const [searchParams] = useSearchParams();
  const page = searchParams.get('page');

  // If ?page=legal, render LegalPage (for OAuth/Firebase compatibility)
  if (page === 'legal') {
    return <LegalPage />;
  }

  // Otherwise render the home page (which handles ?code= param)
  return <HomePage />;
}

function AppContent() {
  return (
    <ProfileProvider>
    <AppLayout>
      <ScrollReset />
      <Routes>
        {/* Main routes */}
        <Route path="/" element={<QueryParamRouter />} />
        <Route path="/picker" element={<ColorPickerPage />} />
        <Route path="/brand-converter" element={<BrandConverterPage />} />
        <Route path="/hex-finder" element={<HexFinderPage />} />
        <Route path="/palettes" element={<MyPalettesPage />} />
        <Route path="/gallery" element={<GalleryPage />} />
        <Route path="/collection" element={<MyCollectionPage />} />
        <Route path="/collection/expand" element={<ExpandCollectionPage />} />
        <Route path="/shades" element={<ShadeFinderPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/suggestions" element={<SuggestionsPage />} />
        <Route path="/profile/:username" element={<ProfilePage />} />

        {/* Fallback to home */}
        <Route path="*" element={<HomePage />} />
      </Routes>
    </AppLayout>
    </ProfileProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppProvider>
        <AuthProvider>
          <BrandsLoader>
            <AppContent />
          </BrandsLoader>
        </AuthProvider>
      </AppProvider>
    </BrowserRouter>
  );
}
